import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactSelect from 'react-select';
import {
  Trophy,
  Award,
  Star,
  Users,
  BookOpen,
  Clock,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  Filter,
  X,
} from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { Pagination } from '../../components/ui/pagination';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  format,
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
} from 'date-fns';
import { useWorkspace } from '../../context/workspace-context';
import { ACTIVITY_TYPE } from '../../constants/activity-types';
import {
  useOrganizationStats,
  useOrganizationActivity,
  useOrganizationCourses,
  useOrganizationPeople,
  useOrganizationStaffPoints,
  useOrganizationCourseDetail,
  useTopPerformers,
  ActivityLogEntry,
} from '../../hooks/use-organization-stats';
import { usePersonDetail } from '../../hooks/use-user-stats';
import { useDepartments } from '../../hooks/use-department';
import { useCourses } from '../../hooks/use-course';
import { AvatarGroup } from '../../components/ui/avatar-group';
import { Checkbox } from '../../components/ui/checkbox';
import { OrganizationTestsTab } from './tests-tab';
import { formatTestDay } from '../../helpers/format-test-date';

const TOP_PERFORMERS_LIMIT = 7;
const PAGE_SIZE = 10;
const COURSE_FILTER_PAGE_SIZE = 100;

export default function OrganizationStatsPage() {
  const navigate = useNavigate();
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();
  const organizationId = currentWorkspace?.id;

  const [activityPage, setActivityPage] = useState(1);
  const [coursesPage, setCoursesPage] = useState(1);
  const [peoplePage, setPeoplePage] = useState(1);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedPersonId, setExpandedPersonId] = useState<number | null>(null);
  const [expandedCourseId, setExpandedCourseId] = useState<number | null>(null);
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [includeTestPoints, setIncludeTestPoints] = useState(false);

  const [pointsPage, setPointsPage] = useState(1);
  const [pointsDraftStartDate, setPointsDraftStartDate] = useState('');
  const [pointsDraftEndDate, setPointsDraftEndDate] = useState('');
  const [pointsStartDate, setPointsStartDate] = useState('');
  const [pointsEndDate, setPointsEndDate] = useState('');
  const [pointsCourseIds, setPointsCourseIds] = useState<number[]>([]);
  const [pointsDepartmentIds, setPointsDepartmentIds] = useState<number[]>([]);
  const [pointsIncludeTests, setPointsIncludeTests] = useState(false);

  const { data: stats, isLoading: statsLoading } =
    useOrganizationStats(organizationId);

  const { data: topPerformers, isLoading: performersLoading } =
    useTopPerformers(
      organizationId,
      TOP_PERFORMERS_LIMIT,
      includeTestPoints,
      appliedStartDate || undefined,
      appliedEndDate || undefined,
    );

  const { data: activity } = useOrganizationActivity(
    organizationId,
    activityPage,
    PAGE_SIZE,
    activeTab === 'activity',
  );

  const { data: courses } = useOrganizationCourses(
    organizationId,
    coursesPage,
    PAGE_SIZE,
    activeTab === 'courses',
  );

  const { data: people } = useOrganizationPeople(
    organizationId,
    peoplePage,
    PAGE_SIZE,
    activeTab === 'people',
  );

  const { data: staffPoints, isLoading: staffPointsLoading } =
    useOrganizationStaffPoints(
      organizationId,
      pointsPage,
      PAGE_SIZE,
      {
        includeTests: pointsIncludeTests,
        startDate: pointsStartDate || undefined,
        endDate: pointsEndDate || undefined,
        courseIds: pointsCourseIds,
        departmentIds: pointsDepartmentIds,
      },
      activeTab === 'points',
    );

  const { data: filterDepartments } = useDepartments(
    organizationId ?? 0,
    activeTab === 'points',
  );
  const departmentFilterOptions = (filterDepartments?.departments ?? []).map(
    (department) => ({ value: department.id, label: department.name }),
  );

  const { data: filterCourses } = useCourses(
    organizationId,
    1,
    COURSE_FILTER_PAGE_SIZE,
    undefined,
    undefined,
    activeTab === 'points',
  );
  const courseFilterOptions = (filterCourses?.courses ?? []).map((course) => ({
    value: course.id,
    label: course.name,
  }));

  const { data: courseDetail, isLoading: courseDetailLoading } =
    useOrganizationCourseDetail(
      organizationId,
      expandedCourseId ?? undefined,
      !!expandedCourseId,
    );

  const { data: personDetail, isLoading: personDetailLoading } =
    usePersonDetail(
      organizationId,
      expandedPersonId ?? undefined,
      !!expandedPersonId,
    );

  const formatDate = (date: string) => {
    const d = new Date(date);
    if (isToday(d)) return 'Today';
    if (isYesterday(d)) return 'Yesterday';
    if (isThisWeek(d)) return format(d, 'EEEE');
    if (isThisMonth(d)) return format(d, 'MMMM d');
    return format(d, 'MMMM d, yyyy');
  };

  const groupActivitiesByDate = (activities: ActivityLogEntry[]) => {
    const groups: { [key: string]: ActivityLogEntry[] } = {};
    activities.forEach((activity) => {
      const date = formatDate(activity.createdAt);
      if (!groups[date]) groups[date] = [];
      groups[date].push(activity);
    });
    return groups;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case ACTIVITY_TYPE.PERFECT_LEVEL:
        return <Star className="h-5 w-5 text-yellow-500" />;
      case ACTIVITY_TYPE.LEVEL_COMPLETED:
        return <Trophy className="h-5 w-5 text-green-500" />;
      case ACTIVITY_TYPE.LEVEL_STARTED:
        return <Award className="h-5 w-5 text-blue-500" />;
      case ACTIVITY_TYPE.COURSE_STARTED:
        return <BookOpen className="h-5 w-5 text-emerald-500" />;
      case ACTIVITY_TYPE.TEST_STARTED:
        return <Clock className="h-5 w-5 text-indigo-500" />;
      case ACTIVITY_TYPE.TEST_COMPLETED:
        return <Trophy className="h-5 w-5 text-purple-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getActivityMessage = (activity: ActivityLogEntry): string => {
    switch (activity.type) {
      case ACTIVITY_TYPE.LEVEL_STARTED:
        return `started level: ${activity.levelName}`;
      case ACTIVITY_TYPE.LEVEL_COMPLETED:
        return `completed level: ${activity.levelName}`;
      case ACTIVITY_TYPE.PERFECT_LEVEL:
        return `perfect score on level: ${activity.levelName}`;
      case ACTIVITY_TYPE.COURSE_STARTED:
        return `started course: ${activity.courseName}`;
      case ACTIVITY_TYPE.TEST_STARTED:
        return 'started a test';
      case ACTIVITY_TYPE.TEST_COMPLETED:
        return 'completed a test';
      default:
        return activity.type;
    }
  };

  if (statsLoading || isLoadingWorkspaces) return <LoadingSpinner />;
  if (!stats) return <div>Error loading stats. Please try again.</div>;

  const activityList =
    activeTab === 'activity' && activity
      ? activity.activities
      : stats.recentActivity;
  const groupedActivities = groupActivitiesByDate(activityList);

  const coursesList =
    activeTab === 'courses' && courses ? courses.courses : stats.courseStats;

  const peopleList = people?.people ?? [];

  return (
    <div className="space-y-8 p-6">
      {/* Organization Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            {stats.organization.name} Statistics
          </h1>
          <p className="text-sm text-muted-foreground">
            Overview of all activity
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-accent-orange relative overflow-hidden rounded-3xl">
            <div className="absolute right-0 top-0 h-full w-24 transform skew-x-12" />
            <CardContent className="p-6">
              <div className="flex flex-col text-title">
                <span className="text-sm opacity-75">Total Users</span>
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5" />
                  <span className="text-2xl font-bold">{stats.totalUsers}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-accent-purple relative overflow-hidden rounded-3xl">
            <div className="absolute right-0 top-0 h-full w-24 transform skew-x-12" />
            <CardContent className="p-6">
              <div className="flex flex-col text-title">
                <span className="text-sm opacity-75">Total Courses</span>
                <div className="flex items-center space-x-2">
                  <BookOpen className="h-5 w-5" />
                  <span className="text-2xl font-bold">
                    {stats.totalCourses}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-accent-pink relative overflow-hidden rounded-3xl">
            <div className="absolute right-0 top-0 h-full w-24 transform skew-x-12" />
            <CardContent className="p-6">
              <div className="flex flex-col text-title">
                <span className="text-sm opacity-75">Total Points</span>
                <div className="flex items-center space-x-2">
                  <Trophy className="h-5 w-5" />
                  <span className="text-2xl font-bold">
                    {stats.totalPoints}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs
        defaultValue="overview"
        className="space-y-4"
        onValueChange={setActiveTab}
      >
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="courses">Courses</TabsTrigger>
          <TabsTrigger value="people">People</TabsTrigger>
          <TabsTrigger value="points">Points</TabsTrigger>
          <TabsTrigger value="tests">Tests</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Course Stats Preview */}
            <Card className="border-none rounded-3xl">
              <CardHeader>
                <CardTitle>Course Statistics</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Top performing courses
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.courseStats.slice(0, 3).map((course) => (
                    <div
                      key={course.id}
                      className="flex items-center gap-4 p-3 rounded-lg bg-accent/5"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium truncate">
                            {course.name}
                          </p>
                          <div className="flex items-center gap-2">
                            <Trophy className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">
                              {course.totalPoints} pts
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {course.totalUsers} users
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Performers */}
            <div className="bg-transparent rounded-3xl">
              <div className="p-6 pb-3">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-accent-orange" />
                    <h2 className="text-lg font-semibold">Top Performers</h2>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {appliedStartDate && appliedEndDate
                      ? `${format(new Date(appliedStartDate), 'd MMM yyyy')} — ${format(new Date(appliedEndDate), 'd MMM yyyy')}`
                      : 'All time'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={draftStartDate}
                    onChange={(e) => setDraftStartDate(e.target.value)}
                    className="h-8 px-2 text-xs border rounded-md bg-background"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <input
                    type="date"
                    value={draftEndDate}
                    onChange={(e) => setDraftEndDate(e.target.value)}
                    className="h-8 px-2 text-xs border rounded-md bg-background"
                  />
                  <button
                    disabled={!draftStartDate || !draftEndDate}
                    onClick={() => {
                      setAppliedStartDate(draftStartDate);
                      setAppliedEndDate(draftEndDate);
                    }}
                    className="h-8 px-3 text-xs font-medium rounded-md bg-accent-purple text-white hover:bg-accent-purple/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    <Filter className="h-3 w-3" />
                    Filter
                  </button>
                  {appliedStartDate && (
                    <button
                      onClick={() => {
                        setDraftStartDate('');
                        setDraftEndDate('');
                        setAppliedStartDate('');
                        setAppliedEndDate('');
                      }}
                      className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-accent/10 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                    <Checkbox
                      checked={includeTestPoints}
                      onCheckedChange={(checked) =>
                        setIncludeTestPoints(checked === true)
                      }
                    />
                    <span>Include tests</span>
                  </label>
                </div>
              </div>
              <div className="p-6 pt-2 space-y-4">
                {performersLoading ? (
                  <div className="flex justify-center py-4">
                    <LoadingSpinner />
                  </div>
                ) : (topPerformers ?? []).length > 0 ? (
                  (topPerformers ?? []).map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-4 p-4 bg-background rounded-3xl"
                    >
                      <div className="h-10 w-10 rounded-full bg-[#FEF6EB] flex items-center justify-center">
                        <span className="text-sm font-medium text-title">
                          {user.name[0]}
                          {user.surname[0]}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <Link
                            to={`/users/${user.id}/stats`}
                            className="text-base font-medium text-title hover:underline"
                          >
                            {user.name} {user.surname}
                          </Link>
                          <div className="flex items-center gap-2">
                            <Trophy className="h-4 w-4 text-accent-orange" />
                            <span className="text-base font-medium text-title">
                              {user.totalPoints} pts
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {appliedStartDate
                      ? 'No activity in this period'
                      : 'No points earned yet'}
                  </p>
                )}
              </div>
            </div>

            {/* Recent Activity Preview */}
            <Card className="border-none rounded-3xl">
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <p className="text-sm text-muted-foreground">Latest actions</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.recentActivity.slice(0, 3).map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center gap-4 p-3 rounded-lg bg-accent/5"
                    >
                      {getActivityIcon(log.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          <span className="text-primary">
                            {log.userName} {log.userSurname}
                          </span>{' '}
                          {getActivityMessage(log)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(
                            new Date(log.createdAt),
                            "dd MMM yyyy 'at' HH:mm",
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="space-y-4">
          {/* Activity Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
              <p className="text-sm text-muted-foreground">
                All organization activity
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {Object.entries(groupedActivities).map(([date, activities]) => (
                  <div key={date} className="relative">
                    <div className="sticky top-0 bg-background/95 backdrop-blur-sm z-10 py-2">
                      <h3 className="text-sm font-medium text-muted-foreground">
                        {date}
                      </h3>
                    </div>
                    <div className="space-y-4 mt-4">
                      {activities.map((log) => (
                        <div
                          key={log.id}
                          className="flex items-start gap-4 p-4 rounded-lg border hover:bg-accent/5 transition-colors"
                        >
                          <div className="mt-1">
                            {getActivityIcon(log.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">
                              <span className="text-primary">
                                {log.userName} {log.userSurname}
                              </span>{' '}
                              {getActivityMessage(log)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {format(
                                new Date(log.createdAt),
                                "dd MMM yyyy 'at' HH:mm",
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <Pagination
                currentPage={activityPage}
                totalPages={activity?.totalPages ?? 0}
                onPageChange={setActivityPage}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="courses" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Courses</CardTitle>
              <p className="text-sm text-muted-foreground">
                Performance across all courses
              </p>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {coursesList.map((course) => {
                  const isExpanded = expandedCourseId === course.id;

                  return (
                    <div key={course.id}>
                      {/* Course Row */}
                      <div
                        className="flex items-center gap-4 px-4 py-4 hover:bg-accent/5 transition-colors cursor-pointer"
                        onClick={() =>
                          setExpandedCourseId(isExpanded ? null : course.id)
                        }
                      >
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <BookOpen className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium truncate">
                            {course.name}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {course.eligibleUsers >= course.totalUsers
                              ? `${course.totalUsers} started of ${course.eligibleUsers} eligible`
                              : `${course.totalUsers} started · Currently unavailable`}
                          </p>
                        </div>
                        <AvatarGroup
                          users={course.startedBy}
                          maxDisplay={5}
                          onUserClick={(userId) =>
                            navigate(`/users/${userId}/stats`)
                          }
                        />
                        <button className="flex items-center justify-center shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </div>

                      {/* Expanded Level Detail (Racetrack) */}
                      <div
                        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
                        style={{
                          gridTemplateRows: isExpanded ? '1fr' : '0fr',
                        }}
                      >
                        <div className="overflow-hidden">
                          <div className="px-4 py-3 bg-accent/5 border-t">
                            {courseDetailLoading ? (
                              <div className="flex justify-center py-4">
                                <LoadingSpinner />
                              </div>
                            ) : courseDetail ? (
                              <div className="space-y-3 pl-14">
                                {/* Not Started Row */}
                                {courseDetail.notStarted.length > 0 && (
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Circle className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm text-muted-foreground">
                                        Not Started
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        ({courseDetail.notStarted.length})
                                      </span>
                                    </div>
                                    <AvatarGroup
                                      users={courseDetail.notStarted}
                                      maxDisplay={5}
                                      onUserClick={(userId) =>
                                        navigate(`/users/${userId}/stats`)
                                      }
                                    />
                                  </div>
                                )}

                                {/* Level Rows */}
                                {courseDetail.levels.map((level) => (
                                  <div
                                    key={level.levelId}
                                    className="flex items-center justify-between"
                                  >
                                    <div className="flex items-center gap-2">
                                      <CheckCircle2
                                        className={`h-4 w-4 ${level.usersAtLevel.length > 0 ? 'text-green-500' : 'text-muted-foreground'}`}
                                      />
                                      <span className="text-sm">
                                        Level {level.levelNumber}:{' '}
                                        {level.levelName}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        ({level.usersAtLevel.length})
                                      </span>
                                    </div>
                                    {level.usersAtLevel.length > 0 && (
                                      <AvatarGroup
                                        users={level.usersAtLevel}
                                        maxDisplay={5}
                                        onUserClick={(userId) =>
                                          navigate(`/users/${userId}/stats`)
                                        }
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Pagination
                currentPage={coursesPage}
                totalPages={courses?.totalPages ?? 0}
                onPageChange={setCoursesPage}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="people" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>People</CardTitle>
              <p className="text-sm text-muted-foreground">
                All employees in this organization
              </p>
            </CardHeader>
            <CardContent>
              {/* Table Header */}
              <div className="grid grid-cols-[1fr_100px_100px_100px_120px_40px] gap-4 px-4 py-2 text-sm font-medium text-muted-foreground border-b">
                <span>Person</span>
                <span className="text-center">Courses</span>
                <span className="text-center">Levels</span>
                <span className="text-right">Points</span>
                <span className="text-right">Avg Test Score</span>
                <span />
              </div>

              <div className="divide-y">
                {peopleList.map((user) => {
                  const isExpanded = expandedPersonId === user.id;

                  return (
                    <div key={user.id}>
                      {/* Person Row */}
                      <div
                        className="grid grid-cols-[1fr_100px_100px_100px_120px_40px] gap-4 items-center px-4 py-3 hover:bg-accent/5 transition-colors cursor-pointer"
                        onClick={() =>
                          setExpandedPersonId(isExpanded ? null : user.id)
                        }
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-sm font-medium text-primary">
                              {user.name[0]}
                              {user.surname[0]}
                            </span>
                          </div>
                          <Link
                            to={`/users/${user.id}/stats`}
                            className="text-sm font-medium truncate hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {user.name} {user.surname}
                          </Link>
                        </div>
                        <span className="text-sm text-center">
                          {user.coursesStarted}
                        </span>
                        <span className="text-sm text-center">
                          {user.levelsCompleted}
                        </span>
                        <span className="text-sm text-right font-medium">
                          {user.totalPoints} pts
                        </span>
                        <span className="text-sm text-right">
                          {user.averageTestScore !== null
                            ? `${user.averageTestScore}%`
                            : '—'}
                        </span>
                        <button className="flex items-center justify-center">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </div>

                      {/* Expanded Detail */}
                      <div
                        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
                        style={{
                          gridTemplateRows: isExpanded ? '1fr' : '0fr',
                        }}
                      >
                        <div className="overflow-hidden">
                          <div className="px-4 py-3 bg-accent/5 border-t">
                            {personDetailLoading ? (
                              <div className="flex justify-center py-4">
                                <LoadingSpinner />
                              </div>
                            ) : personDetail ? (
                              <div className="space-y-4 pl-12">
                                {/* Test Summary */}
                                {personDetail.testSummary.totalTests > 0 ? (
                                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span>
                                      {personDetail.testSummary.totalTests}{' '}
                                      {personDetail.testSummary.totalTests === 1
                                        ? 'test'
                                        : 'tests'}
                                    </span>
                                    <span>·</span>
                                    <span>
                                      Last:{' '}
                                      {formatTestDay(
                                        personDetail.testSummary.lastTestDate!,
                                        personDetail.testSummary
                                          .isLastTestGenerated,
                                      )}
                                    </span>
                                    <span>·</span>
                                    <span>
                                      Avg score:{' '}
                                      {personDetail.testSummary.averageScore}%
                                    </span>
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground">
                                    No tests yet
                                  </p>
                                )}

                                {/* Courses */}
                                {personDetail.courses.length > 0 ? (
                                  personDetail.courses.map((course) => (
                                    <div key={course.courseId}>
                                      <p className="text-sm font-medium mb-2">
                                        {course.courseName} —{' '}
                                        <span className="text-muted-foreground">
                                          {course.completedLevels}/
                                          {course.totalLevels} levels
                                        </span>
                                      </p>
                                      <div className="space-y-1 pl-4">
                                        {course.levels.map((level) => (
                                          <div
                                            key={level.levelId}
                                            className="flex items-center gap-2 text-sm"
                                          >
                                            {level.isCompleted ? (
                                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                                            ) : (
                                              <Circle className="h-4 w-4 text-muted-foreground" />
                                            )}
                                            <span
                                              className={
                                                level.isCompleted
                                                  ? 'text-foreground'
                                                  : 'text-muted-foreground'
                                              }
                                            >
                                              Level {level.levelNumber}:{' '}
                                              {level.levelName}
                                            </span>
                                            {level.scorePercent !== null && (
                                              <span className="text-xs text-muted-foreground">
                                                — {level.scorePercent}%
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-sm text-muted-foreground">
                                    No courses started yet
                                  </p>
                                )}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Pagination
                currentPage={peoplePage}
                totalPages={people?.totalPages ?? 0}
                onPageChange={setPeoplePage}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="points" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Points</CardTitle>
              <p className="text-sm text-muted-foreground">
                Points earned by everyone in this organization
              </p>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={pointsDraftStartDate}
                    onChange={(e) => setPointsDraftStartDate(e.target.value)}
                    className="h-8 px-2 text-xs border rounded-md bg-background"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <input
                    type="date"
                    value={pointsDraftEndDate}
                    onChange={(e) => setPointsDraftEndDate(e.target.value)}
                    className="h-8 px-2 text-xs border rounded-md bg-background"
                  />
                  <button
                    disabled={!pointsDraftStartDate || !pointsDraftEndDate}
                    onClick={() => {
                      setPointsStartDate(pointsDraftStartDate);
                      setPointsEndDate(pointsDraftEndDate);
                      setPointsPage(1);
                    }}
                    className="h-8 px-3 text-xs font-medium rounded-md bg-accent-purple text-white hover:bg-accent-purple/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    <Filter className="h-3 w-3" />
                    Filter
                  </button>
                  {pointsStartDate && (
                    <button
                      onClick={() => {
                        setPointsDraftStartDate('');
                        setPointsDraftEndDate('');
                        setPointsStartDate('');
                        setPointsEndDate('');
                        setPointsPage(1);
                      }}
                      className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-accent/10 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {pointsStartDate && pointsEndDate
                      ? `${format(new Date(pointsStartDate), 'd MMM yyyy')} — ${format(new Date(pointsEndDate), 'd MMM yyyy')}`
                      : 'All time'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-[220px]">
                    <ReactSelect
                      isMulti
                      options={courseFilterOptions}
                      value={courseFilterOptions.filter((option) =>
                        pointsCourseIds.includes(option.value),
                      )}
                      onChange={(selectedOptions) => {
                        const courseIds = (selectedOptions ?? []).map(
                          (option) => option.value,
                        );
                        setPointsCourseIds(courseIds);
                        setPointsPage(1);
                      }}
                      placeholder="All courses"
                    />
                  </div>
                  <div className="w-[220px]">
                    <ReactSelect
                      isMulti
                      options={departmentFilterOptions}
                      value={departmentFilterOptions.filter((option) =>
                        pointsDepartmentIds.includes(option.value),
                      )}
                      onChange={(selectedOptions) => {
                        const departmentIds = (selectedOptions ?? []).map(
                          (option) => option.value,
                        );
                        setPointsDepartmentIds(departmentIds);
                        setPointsPage(1);
                      }}
                      placeholder="All teams"
                    />
                  </div>
                  <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                    <Checkbox
                      checked={pointsIncludeTests}
                      onCheckedChange={(checked) => {
                        setPointsIncludeTests(checked === true);
                        setPointsPage(1);
                      }}
                    />
                    <span>Include tests</span>
                  </label>
                </div>
              </div>

              {/* Table Header */}
              <div className="grid grid-cols-[60px_1fr_120px] gap-4 px-4 py-2 text-sm font-medium text-muted-foreground border-b">
                <span>Rank</span>
                <span>Person</span>
                <span className="text-right">Points</span>
              </div>

              {staffPointsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="divide-y">
                  {(staffPoints?.staffPoints ?? []).map((person) => (
                    <div
                      key={person.id}
                      className="grid grid-cols-[60px_1fr_120px] gap-4 items-center px-4 py-3 hover:bg-accent/5 transition-colors"
                    >
                      <span className="text-sm text-muted-foreground">
                        #{person.rank}
                      </span>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="text-sm font-medium text-primary">
                            {person.name[0]}
                            {person.surname[0]}
                          </span>
                        </div>
                        <Link
                          to={`/users/${person.id}/stats`}
                          className="text-sm font-medium truncate hover:underline"
                        >
                          {person.name} {person.surname}
                        </Link>
                      </div>
                      <span className="text-sm text-right font-medium">
                        {person.totalPoints} pts
                      </span>
                    </div>
                  ))}
                  {(staffPoints?.staffPoints ?? []).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No people match these filters
                    </p>
                  )}
                </div>
              )}
              <Pagination
                currentPage={pointsPage}
                totalPages={staffPoints?.totalPages ?? 0}
                onPageChange={setPointsPage}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tests" className="space-y-4">
          <OrganizationTestsTab
            organizationId={organizationId}
            pageSize={PAGE_SIZE}
            isActive={activeTab === 'tests'}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
